import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ClinicProvider } from "@/context/ClinicContext";
import { NotificationProvider } from "@/context/NotificationContext";
import ProtectedRoute, { PatientRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import PatientLayout from "@/components/PatientLayout";
import Login from "@/pages/Login";
import PatientLogin from "@/pages/PatientLogin";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientProfile from "@/pages/PatientProfile";
import Labs from "@/pages/Labs";
import Messages from "@/pages/Messages";
import AuditLog from "@/pages/AuditLog";
import Compliance from "@/pages/Compliance";
import Incidents from "@/pages/Incidents";
import IncidentDetail from "@/pages/IncidentDetail";
import Settings from "@/pages/Settings";
import Schedule from "@/pages/Schedule";
import Today from "@/pages/Today";
import Snapshot from "@/pages/Snapshot";
import PatientHome from "@/pages/patient/PatientHome";
import PatientTreatment from "@/pages/patient/PatientTreatment";
import PatientLabs from "@/pages/patient/PatientLabs";
import PatientMembership from "@/pages/patient/PatientMembership";
import PatientMessages from "@/pages/patient/PatientMessages";
import PatientCheckin from "@/pages/patient/PatientCheckin";
import PatientTimeline from "@/pages/patient/PatientTimeline";
import PatientPrivacy from "@/pages/patient/PatientPrivacy";
import PatientProgress from "@/pages/patient/PatientProgress";
import PatientBook from "@/pages/patient/PatientBook";
import PatientAppointments from "@/pages/patient/PatientAppointments";
import { Toaster } from "@/components/ui/sonner";

const Staff = ({ children }) => (
  <ProtectedRoute>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

const Patient = ({ children }) => (
  <PatientRoute>
    <PatientLayout>{children}</PatientLayout>
  </PatientRoute>
);

function App() {
  return (
    <div className="App">
      {/* Both providers sit INSIDE the router: which portal a request means is a
          function of the route, and AuthProvider has to re-resolve identity when you
          cross between them. */}
      <BrowserRouter>
        <AuthProvider>
          <ClinicProvider>
            {/* One notification loop for the whole app, above the routes so navigating
                between staff pages does not reset what it has already announced. It
                gates itself on a staff session — see NotificationContext. */}
            <NotificationProvider>
            <Routes>
              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/patient/login" element={<PatientLogin />} />

              {/* Clinic Portal (staff) */}
              <Route path="/" element={<Staff><Dashboard /></Staff>} />
              <Route path="/patients" element={<Staff><Patients /></Staff>} />
              <Route path="/patients/:id" element={<Staff><PatientProfile /></Staff>} />
              <Route path="/today" element={<Staff><Today /></Staff>} />
              <Route path="/snapshot" element={<Staff><Snapshot /></Staff>} />
              <Route path="/schedule" element={<Staff><Schedule /></Staff>} />
              <Route path="/labs" element={<Staff><Labs /></Staff>} />
              <Route path="/messages" element={<Staff><Messages /></Staff>} />
              <Route path="/audit" element={<Staff><AuditLog /></Staff>} />
              <Route path="/compliance" element={<Staff><Compliance /></Staff>} />
              <Route path="/incidents" element={<Staff><Incidents /></Staff>} />
              <Route path="/incidents/:id" element={<Staff><IncidentDetail /></Staff>} />
              <Route path="/settings" element={<Staff><Settings /></Staff>} />

              {/* Patient Portal */}
              <Route path="/portal" element={<Patient><PatientHome /></Patient>} />
              <Route path="/portal/treatment" element={<Patient><PatientTreatment /></Patient>} />
              <Route path="/portal/labs" element={<Patient><PatientLabs /></Patient>} />
              <Route path="/portal/membership" element={<Patient><PatientMembership /></Patient>} />
              <Route path="/portal/messages" element={<Patient><PatientMessages /></Patient>} />
              <Route path="/portal/checkin" element={<Patient><PatientCheckin /></Patient>} />
              <Route path="/portal/timeline" element={<Patient><PatientTimeline /></Patient>} />
              <Route path="/portal/book" element={<Patient><PatientBook /></Patient>} />
              <Route path="/portal/appointments" element={<Patient><PatientAppointments /></Patient>} />
              <Route path="/portal/progress" element={<Patient><PatientProgress /></Patient>} />
              <Route path="/portal/privacy" element={<Patient><PatientPrivacy /></Patient>} />
            </Routes>
            </NotificationProvider>
            <Toaster />
          </ClinicProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
